import { api } from "@/lib/api-client"

// =============================================================================
// Middleware API (backend: routes/admin/middleware.py)
// =============================================================================

export interface MiddlewareEntry {
  id: string
  type: string
  enabled: boolean
  config: Record<string, unknown>
  [key: string]: unknown
}

export interface AgentMiddlewareResponse {
  agent: string
  middleware: MiddlewareEntry[]
  /** Stable SHA-256 of the middleware list; pass back as expected_hash on PUT. */
  hash: string
}

export async function getAgentMiddleware(agentName: string): Promise<AgentMiddlewareResponse> {
  const response = await api.get(`/admin/agents/${encodeURIComponent(agentName)}/middleware`)
  return response.data || response
}

export async function createMiddlewareEntry(
  agentName: string,
  entry: Omit<MiddlewareEntry, "id"> & { id?: string }
): Promise<AgentMiddlewareResponse & { created: MiddlewareEntry }> {
  const response = await api.post(
    `/admin/agents/${encodeURIComponent(agentName)}/middleware`,
    entry
  )
  const data = response.data || response

  if (data.success === false) {
    throw new Error(data.message || data.error || "Failed to create middleware entry")
  }

  return data
}

export interface UpdateMiddlewareEntryArgs {
  agentName: string
  middlewareId: string
  entry: Partial<MiddlewareEntry> & { type: string }
  expectedHash?: string
  source?: string
  reason?: string
}

export async function updateMiddlewareEntry(
  args: UpdateMiddlewareEntryArgs
): Promise<AgentMiddlewareResponse & { updated: MiddlewareEntry }> {
  const { agentName, middlewareId, entry, expectedHash, source, reason } = args
  const body: Record<string, unknown> = { entry }
  if (expectedHash) body.expected_hash = expectedHash
  if (source) body.source = source
  if (reason) body.reason = reason

  const response = await api.put(
    `/admin/agents/${encodeURIComponent(agentName)}/middleware/${encodeURIComponent(middlewareId)}`,
    body
  )
  const data = response.data || response

  if (data.success === false) {
    throw new Error(data.message || data.error || "Failed to update middleware entry")
  }

  return data
}

export async function deleteMiddlewareEntry(
  agentName: string,
  middlewareId: string
): Promise<AgentMiddlewareResponse & { deleted: MiddlewareEntry }> {
  const response = await api.delete(
    `/admin/agents/${encodeURIComponent(agentName)}/middleware/${encodeURIComponent(middlewareId)}`
  )
  const data = response.data || response

  if (data.success === false) {
    throw new Error(data.message || data.error || "Failed to delete middleware entry")
  }

  return data
}

/**
 * Replace the entire middleware list for an agent from Raw JSON mode.
 *
 * Requires:
 * - ``expectedHash`` from the most recent GET — 409 if stale.
 * - ``reason`` with at least 10 characters — stored in the audit entry.
 */
export async function replaceMiddlewareRaw(
  agentName: string,
  middleware: MiddlewareEntry[],
  options: { expectedHash: string; reason: string; source?: string }
): Promise<AgentMiddlewareResponse> {
  const response = await api.put(`/admin/agents/${encodeURIComponent(agentName)}/middleware/raw`, {
    middleware,
    expected_hash: options.expectedHash,
    reason: options.reason,
    source: options.source || "raw",
  })
  const data = response.data || response

  if (data.success === false) {
    throw new Error(data.message || data.error || "Failed to replace middleware list")
  }

  return data
}
